import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faCircleMinus, faSquareCheck, faSquareMinus } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type AnyObject } from '@Types';
import { type TodoItem } from 'AdmissionsGuidance/StudentDashboard.types';
import clsx from 'clsx';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from 'Text';
import { InlineLink } from 'InlineLink';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { enrollmentSubTodoVisible } from './helpers';

function EnrollmentTodoItemComponent({ item, showCompletedTodos }: { item: TodoItem; showCompletedTodos: boolean }) {
    const inReview = !!item.inReview?.();
    const needsMoreInfo = !!item.needsMoreInfo?.();
    const completed = item.hasBeenCompleted();
    const irrelevant = !!item.irrelevant?.();
    const disabled = item.disabled();
    const isLoading = !!item.isLoading?.();
    const hasSubTodos = !!item.subTodosConfig?.getSubTodos?.()?.length;
    const handleClick = useCallback(() => {
        if (completed || disabled) return;
        item.onClick();
    }, [item, completed, disabled]);

    return (
        <EnrollmentTodoItemPresentation
            item={item}
            showCompletedTodos={showCompletedTodos}
            inReview={inReview}
            needsMoreInfo={needsMoreInfo}
            completed={completed}
            irrelevant={irrelevant}
            disabled={disabled}
            isLoading={isLoading}
            hasSubTodos={hasSubTodos}
            onClick={handleClick}
        />
    );
}

function getIcon({
    completed,
    irrelevant,
    inReview,
    needsMoreInfo,
}: {
    completed: boolean;
    irrelevant: boolean;
    inReview: boolean;
    needsMoreInfo: boolean;
}): { icon: IconProp; subTodoIcon: IconProp; colorClass: string } {
    if (completed) return { icon: faCircleCheck, subTodoIcon: faSquareCheck, colorClass: 'text-slate-grey' };
    if (irrelevant || inReview || needsMoreInfo) {
        return { icon: faCircleMinus, subTodoIcon: faSquareMinus, colorClass: 'text-slate-grey' };
    }
    return { icon: faCircle, subTodoIcon: faSquare, colorClass: 'text-primary' };
}

function EnrollmentTodoItemPresentation({
    item,
    showCompletedTodos,
    inReview,
    needsMoreInfo,
    completed,
    irrelevant,
    disabled,
    isLoading,
    hasSubTodos,
    onClick,
}: {
    item: TodoItem;
    showCompletedTodos: boolean;
    inReview: boolean;
    needsMoreInfo: boolean;
    completed: boolean;
    irrelevant: boolean;
    disabled: boolean;
    isLoading: boolean;
    hasSubTodos: boolean;
    onClick: () => void;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'enrollmentBox' });
    const { icon, colorClass, subTodoIcon } = useMemo(
        () => getIcon({ completed, irrelevant, inReview, needsMoreInfo }),
        [completed, irrelevant, inReview, needsMoreInfo],
    );
    const shouldShowLink = useMemo(() => !completed && !irrelevant && !inReview, [completed, irrelevant, inReview]);
    const text = useMemo(
        () =>
            `${t(item.localeKey)}${needsMoreInfo ? ` ${t('needs_more_information')}` : ''}${
                inReview && !needsMoreInfo ? ` ${t('in_review')}` : ''
            }`,
        [item.localeKey, needsMoreInfo, inReview, t],
    );
    const words = text.split(' ');
    const lastWord = words.pop();
    const firstWords = words.join(' ');

    return (
        <>
            <div className="mb-2.5 ms-2.5 flex items-start justify-start gap-[5px] last:mb-0">
                <span className="todo-icon mt-[3px] shrink-0 align-middle text-sm text-white">
                    <FontAwesomeIcon className={clsx(colorClass)} icon={icon} />
                </span>
                {!isLoading && (
                    <span
                        className={clsx(
                            'todo-text flex justify-start',
                            disabled && shouldShowLink ? 'cursor-not-allowed text-opacity-50' : '',
                        )}
                    >
                        {shouldShowLink ? (
                            <InlineLink onPress={onClick} disabled={disabled} className="text-start text-base">
                                {firstWords}
                                {firstWords && ' '}
                                <span className="whitespace-nowrap">
                                    {lastWord}
                                    <FontAwesomeIcon className="ms-1 inline-block rtl:rotate-180" icon={faArrowRight} />
                                </span>
                            </InlineLink>
                        ) : (
                            <Text as="div" color="slate-grey">
                                {text}
                            </Text>
                        )}
                    </span>
                )}
                {isLoading && <FrontRoyalSpinner className="m-0" color="force-brand" />}
            </div>
            {hasSubTodos &&
                item.subTodosConfig!.getSubTodos().map((subTodo: AnyObject, i: number) =>
                    enrollmentSubTodoVisible(subTodo, item, showCompletedTodos) ? (
                        <div
                            // oxlint-disable-next-line react/no-array-index-key
                            key={i}
                            className="sub-todo ms-7.5 flex items-start justify-start gap-2"
                        >
                            <span className="todo-icon mt-[2px] shrink-0 align-middle">
                                <FontAwesomeIcon className="text-slate-grey" icon={subTodoIcon} />
                            </span>
                            <span className="todo-text">
                                <Text as="div" color="slate-grey" className="text-base">
                                    {item.subTodosConfig!.getSubTodoText(subTodo)}
                                    {item.subTodosConfig!.inReview(subTodo) && (
                                        <span className="text-sm">&nbsp;{t('in_review')}</span>
                                    )}
                                </Text>
                            </span>
                        </div>
                    ) : null,
                )}
        </>
    );
}

export const EnrollmentTodoItem = memo(EnrollmentTodoItemComponent) as typeof EnrollmentTodoItemComponent;
